import { SxProps, Theme } from '@mui/material/styles';
import { BaseDialogProps, BaseDialogMode } from '../../dialogs/BaseDialog/BaseDialog.model';

/**
 * Dialog variants for different use cases
 */
export type DialogVariant = 'default' | 'fullscreen' | 'compact';

/**
 * Unique identifier for dialogs in the stack
 */
export type DialogId = string;

/**
 * Dialog state within the provider
 */
export interface DialogState {
  /**
   * Unique identifier for the dialog
   */
  id: DialogId;

  /**
   * Whether the dialog is currently open
   */
  isOpen: boolean;

  /**
   * Dialog configuration
   */
  config: DialogConfig;

  /**
   * Assigned z-index for this dialog
   */
  zIndex: number;

  /**
   * Parent dialog ID if this is a nested dialog
   */
  parentId?: DialogId;

  /**
   * Child dialog IDs for nested dialog management
   */
  childIds: DialogId[];

  /**
   * Timestamp when dialog was opened (for ordering)
   */
  openedAt: number;

  /**
   * Element that had focus before dialog opened (for restoration)
   */
  previousFocusElement?: HTMLElement;
}

/**
 * Configuration for individual dialogs
 */
export interface DialogConfig {
  /**
   * Dialog mode: modal or embedded
   */
  mode?: BaseDialogMode;

  /**
   * Dialog variant for styling
   */
  variant?: DialogVariant;

  /**
   * Maximum width of the dialog
   */
  maxWidth?: BaseDialogProps['maxWidth'];

  /**
   * Whether to disable backdrop click to close
   */
  disableBackdropClick?: boolean;

  /**
   * Whether to disable ESC key to close
   */
  disableEscapeKeyDown?: boolean;

  /**
   * Custom z-index (overrides automatic assignment)
   */
  zIndex?: number;

  /**
   * Help content ID for this dialog
   */
  helpId?: string;

  /**
   * Whether this dialog should persist when parent closes
   */
  persistOnParentClose?: boolean;

  /**
   * Custom styling for the dialog
   */
  sx?: SxProps<Theme>;

  /**
   * Callback when dialog opens
   */
  onOpen?: () => void;

  /**
   * Callback when dialog closes
   */
  onClose?: (reason?: string) => void;

  /**
   * Callback when z-index changes
   */
  onZIndexChange?: (zIndex: number) => void;
}

/**
 * Global configuration for all dialogs
 */
export interface DialogGlobalConfig {
  /**
   * Base z-index for the first dialog
   */
  baseZIndex: number;

  /**
   * Increment amount for each nested dialog
   */
  zIndexIncrement: number;

  /**
   * Maximum number of dialogs in stack
   */
  maxStackDepth: number;

  /**
   * Default mode for new dialogs
   */
  defaultMode: BaseDialogMode;

  /**
   * Default variant for new dialogs
   */
  defaultVariant: DialogVariant;

  /**
   * Default maximum width
   */
  defaultMaxWidth: BaseDialogProps['maxWidth'];

  /**
   * Whether to restore focus when dialogs close
   */
  restoreFocus: boolean;

  /**
   * Whether to close all dialogs when ESC is pressed
   */
  escapeClosesAll: boolean;

  /**
   * Theme-aware default styles
   */
  defaultStyles?: {
    paper?: SxProps<Theme>;
    backdrop?: SxProps<Theme>;
  };
}

/**
 * Dialog stack information
 */
export interface DialogStack {
  /**
   * Ordered list of dialog IDs (bottom to top)
   */
  order: DialogId[];

  /**
   * Currently active (top) dialog ID
   */
  activeId: DialogId | null;

  /**
   * Total number of open dialogs
   */
  count: number;

  /**
   * Next available z-index
   */
  nextZIndex: number;
}

/**
 * Help system integration
 */
export interface DialogHelpSystem {
  /**
   * Get help content for a given help ID
   */
  getContent: (helpId: string) => Promise<string | null>;

  /**
   * Register help content
   */
  registerContent: (helpId: string, content: string) => void;

  /**
   * Check if help content exists
   */
  hasContent: (helpId: string) => boolean;
}

/**
 * Dialog provider context interface
 */
export interface DialogProviderContext {
  // State
  /**
   * Map of all registered dialogs
   */
  dialogs: Map<DialogId, DialogState>;

  /**
   * Current dialog stack information
   */
  stack: DialogStack;

  /**
   * Global configuration
   */
  globalConfig: DialogGlobalConfig;

  /**
   * Help system integration
   */
  helpSystem: DialogHelpSystem;

  // Actions
  /**
   * Register a new dialog with the provider
   */
  registerDialog: (id: DialogId, config?: Partial<DialogConfig>) => void;

  /**
   * Unregister a dialog from the provider
   */
  unregisterDialog: (id: DialogId) => void;

  /**
   * Open a dialog and add it to the stack
   */
  openDialog: (id: DialogId, parentId?: DialogId) => void;

  /**
   * Close a dialog and remove it from the stack
   */
  closeDialog: (id: DialogId, reason?: string) => void;

  /**
   * Close all dialogs in the stack
   */
  closeAllDialogs: (reason?: string) => void;

  /**
   * Close the topmost dialog
   */
  closeTopDialog: (reason?: string) => void;

  /**
   * Get the z-index for a specific dialog
   */
  getZIndex: (id: DialogId) => number;

  /**
   * Get the current top dialog ID
   */
  getTopDialogId: () => DialogId | null;

  /**
   * Check if a dialog is currently open
   */
  isDialogOpen: (id: DialogId) => boolean;

  /**
   * Update dialog configuration
   */
  updateDialogConfig: (id: DialogId, config: Partial<DialogConfig>) => void;

  /**
   * Update global configuration
   */
  updateGlobalConfig: (config: Partial<DialogGlobalConfig>) => void;

  /**
   * Handle ESC key press
   */
  handleEscapeKey: () => void;

  /**
   * Handle backdrop click
   */
  handleBackdropClick: (id: DialogId) => void;
}

/**
 * Action types for the dialog reducer
 */
export enum DialogActionType {
  REGISTER_DIALOG = 'REGISTER_DIALOG',
  UNREGISTER_DIALOG = 'UNREGISTER_DIALOG',
  OPEN_DIALOG = 'OPEN_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
  CLOSE_ALL_DIALOGS = 'CLOSE_ALL_DIALOGS',
  UPDATE_DIALOG_CONFIG = 'UPDATE_DIALOG_CONFIG',
  UPDATE_GLOBAL_CONFIG = 'UPDATE_GLOBAL_CONFIG',
  UPDATE_Z_INDEX = 'UPDATE_Z_INDEX',
  SET_FOCUS_ELEMENT = 'SET_FOCUS_ELEMENT',
}

/**
 * Dialog provider actions
 */
export interface DialogAction {
  type: DialogActionType;
  payload?: any;
}

/**
 * Dialog provider state
 */
export interface DialogProviderState {
  dialogs: Map<DialogId, DialogState>;
  globalConfig: DialogGlobalConfig;
}

/**
 * Default global configuration
 */
export const DEFAULT_DIALOG_GLOBAL_CONFIG: DialogGlobalConfig = {
  baseZIndex: 1300, // Material-UI standard modal z-index
  zIndexIncrement: 10,
  maxStackDepth: 10,
  defaultMode: 'modal',
  defaultVariant: 'default',
  defaultMaxWidth: 'sm',
  restoreFocus: true,
  escapeClosesAll: false, // Only close top dialog by default
};

/**
 * Default dialog configuration
 */
export const DEFAULT_DIALOG_CONFIG: DialogConfig = {
  mode: 'modal',
  variant: 'default',
  maxWidth: 'sm',
  disableBackdropClick: false,
  disableEscapeKeyDown: false,
  persistOnParentClose: false,
};